import React, { useState, useEffect, useCallback } from 'react';
import OnavChart from './OnavChart';
import WineAutocomplete from './WineAutocomplete';
import AttachmentPicker from './AttachmentPicker';
import LabelScanner from './LabelScanner';
import { applyScanToWineForm, uploadWineLabel } from './labelScan';
import { useI18n } from './i18n';
import { exportPdf } from './pdfExport';
import { normalizeVariant, suggestVariant, layoutOf, clampToVariant, calcMarksTotals, printedValues, isPrinted, nonPrintedRows, ALL_MARKS } from './onavVariants';
import OnavHelpPanel from './OnavHelpPanel';
import { helpOf, SECTION_WEIGHTS } from './onavHelp';
import { colorByKey, colorsOf, swatchBackground } from './onavColors';
import {
  WINE_TYPES, CLASSIFICATIONS, AROMA_TYPES, VERDICTS, parseAromaTypes, joinAromaTypes,
} from './codes';

const DRAFT_KEY = 'tnh2_draft_onav';

const INITIAL = {
  wine_name: '', classification: '', wine_type: '', vintage: '',
  producer: '', abv: '', serving_temperature: '',
  tasting_date: new Date().toISOString().slice(0, 10),
  venue: '', taster: '',
  variant: 'still',
  // marks start NOT selected (null): nothing scored yet, counted and sent as 0; a stored 0 of an edited sheet is a selected 0
  visual_hue: null, visual_intensity: null, visual_clarity: null, perlage_size: null, perlage_persistence: null,
  olfactory_cleanness: null, olfactory_intensity: null, olfactory_finesse: null, olfactory_complexity: null,
  aroma_types: '',
  gustatory_cleanness: null, gustatory_intensity: null, gustatory_body: null, gustatory_harmony: null,
  balance_acidity: '', balance_tannicity: '', balance_alcohol: '', balance_softness: '',
  gustatory_persistence: null,
  gustatory_aftertaste: null, overall_impression: null,
  notes: '',
  wine_color: '',
  event: '', commission_number: '', sample_number: '', presentation_category: '', tasting_time: '',
  visibility: 'private',
};

function loadDraft() {
  try {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) {
      const draft = { ...INITIAL, ...JSON.parse(saved) };
      // drafts of the slider version hold 0 on every row that was never touched: show them as not selected (counted as 0 either way)
      ALL_MARKS.forEach((k) => { if (draft[k] === 0) draft[k] = null; });
      return draft;
    }
  } catch { /* ignore */ }
  return INITIAL;
}

// Totals out of 100 for the variant of the sheet: still = visual /18, olfactory /30, gustatory /44, final /8;
// sparkling = visual /30, olfactory /28, gustatory /35, final /7 (see onavVariants.js)
function calcTotals(f) {
  const { visual, olfactory, gustatory, final, total } = calcMarksTotals(f, f.variant);
  const verdict = VERDICTS.find((v) => total >= v.min)?.code || '';
  return { visual, olfactory, gustatory, final, total, verdict };
}

// Colour of the wine, used by the official form (white / rosé / red)
const WINE_COLORS = ['white', 'rose', 'red'];
function WineColorSelect({ value, onChange }) {
  const { t, tc } = useI18n();
  return (
    <div className="form-field">
      <label>{t('onav.f.wine_color')}</label>
      <select name="wine_color" value={value} onChange={onChange}>
        <option value="">—</option>
        {WINE_COLORS.map((c) => <option key={c} value={c}>{tc('wine_type', c)}</option>)}
      </select>
      <small className="import-hint">{t('onav.f.wine_color_hint')}</small>
    </div>
  );
}

const isBlankMark = (v) => v === undefined || v === null || v === '';

// One row of the official form: the values PRINTED on the row as chips, highest first, like the columns of the paper form
// (8 7 6 5 4 2 0 on the still 8-point rows: no 3 and no 1). A radio group: arrow keys move and select, Home/End jump.
function MarkChips({ name, value, max, variant, onChange }) {
  const { t } = useI18n();
  const [showInfo, setShowInfo] = React.useState(false);
  const refs = React.useRef([]);
  const label = t(`onav.p.${name}`);
  const hasHelp = !!helpOf(name);
  const panelId = `onav-help-${name}`;
  const printed = printedValues(variant, name);
  const blank = isBlankMark(value);
  const legacy = !blank && !isPrinted(variant, name, value);      // saved before the printed-values rule: nothing is selected, the user must choose
  const selected = !blank && !legacy ? Number(value) : null;
  const tabbable = selected !== null ? printed.indexOf(selected) : 0;

  const choose = (index) => {
    const next = Math.max(0, Math.min(printed.length - 1, index));
    onChange(name, printed[next]);
    if (refs.current[next]) refs.current[next].focus();
  };
  const onKeyDown = (e, index) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); choose(index + 1); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); choose(index - 1); }
    else if (e.key === 'Home') { e.preventDefault(); choose(0); }
    else if (e.key === 'End') { e.preventDefault(); choose(printed.length - 1); }
  };

  return (
    <div className={`pairing-slider onav-mark-row${legacy ? ' legacy' : ''}`} id={`onav-mark-${name}`}>
      <label id={`onav-mark-label-${name}`}>
        <span className="pairing-label food">{label}</span>
        <span className="pairing-value">{selected !== null ? selected : '—'}/{max}</span>
        {hasHelp && (
          <button type="button" className="info-btn" aria-expanded={showInfo} aria-controls={panelId}
            aria-label={`${label}: ${t('onav.help.open')}`}
            onClick={(e) => { e.preventDefault(); setShowInfo(!showInfo); }}
            title={t('onav.help.open')}>?</button>
        )}
      </label>
      <div className="onav-marks" role="radiogroup" aria-labelledby={`onav-mark-label-${name}`}>
        {printed.map((v, i) => (
          <button type="button" key={v} role="radio" aria-checked={selected === v} data-value={v}
            tabIndex={i === tabbable ? 0 : -1} ref={(el) => { refs.current[i] = el; }}
            className={`onav-mark-chip${selected === v ? ' selected' : ''}`}
            onClick={() => onChange(name, v)} onKeyDown={(e) => onKeyDown(e, i)}>{v}</button>
        ))}
      </div>
      {legacy && <div className="onav-mark-warning" role="alert">{t('onav.mark.not_printed', { value, values: printed.join(', ') })}</div>}
      {showInfo && hasHelp && <OnavHelpPanel field={name} max={max} id={panelId} onClose={() => setShowInfo(false)} />}
    </div>
  );
}

// Palettes of the hue reference: keys of the official ONAV colour chart (onavColors.js; labels are the `onav.color.<key>` translations).
// white / passito / rose / red are the four categories of the chart; for sparkling wines (not a category of the chart) a selection of them.
const PALETTE_KEYS = {
  sparkling: ['greenish', 'pale_straw', 'straw_yellow', 'golden_yellow', 'pale_pink', 'coral_pink'],
  semi_sparkling: ['greenish', 'straw_yellow', 'golden_yellow', 'medium_ruby', 'ruby_red'],
};
const paletteFor = (type) => (PALETTE_KEYS[type] ? PALETTE_KEYS[type].map(colorByKey) : colorsOf(['white', 'passito', 'rose', 'red'].includes(type) ? type : 'white'));

function ColorPalette({ wineType }) {
  const { t, tc } = useI18n();
  const type = wineType || 'white';
  const palette = paletteFor(type);
  const [selected, setSelected] = React.useState(null);
  const colorLabel = (c) => t(`onav.color.${c.key}`);

  return (
    <div className="color-palette">
      <span className="color-palette-label">{t('onav.palette_ref', { tip: tc('wine_type', type) })}</span>
      {selected !== null && palette[selected] && (
        <div className="color-zoom" onClick={() => setSelected(null)}>
          <div className="color-zoom-circle" style={{ background: swatchBackground(palette[selected]) }} />
          <div className="color-zoom-info">
            <span className="color-zoom-label">{colorLabel(palette[selected])}</span>
            <span className="color-zoom-hex">{palette[selected].hex}</span>
          </div>
        </div>
      )}
      <div className="color-swatches">
        {palette.map((c, i) => (
          <div key={c.key} className={`color-swatch ${selected === i ? 'active' : ''}`} role="button" tabIndex={0}
            onClick={() => setSelected(selected === i ? null : i)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(selected === i ? null : i); } }}
            title={colorLabel(c)}>
            <div className="swatch-circle" style={{ background: swatchBackground(c) }} />
            <span className="swatch-label">{colorLabel(c)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Form state from an API sheet (null values keep the empty default)
function fromSheet(sheet) {
  const out = { ...INITIAL };
  Object.keys(INITIAL).forEach((k) => {
    if (sheet[k] !== undefined && sheet[k] !== null) out[k] = sheet[k];
  });
  return out;
}

// '' -> null so that optional codes and texts are accepted by the API validation
const emptyToNull = (value) => (value === '' || value === undefined ? null : value);

function OnavForm({ onSubmit, initialData, onCancel }) {
  const { t, tc } = useI18n();
  const editing = !!initialData;
  const [form, setForm] = useState(() => (initialData ? fromSheet(initialData) : loadDraft()));
  const [attachmentItems, setAttachmentItems] = useState([]);
  const [labelPhoto, setLabelPhoto] = useState(null); // scanned label photo, uploaded to the wine catalog after saving
  const [variantTouched, setVariantTouched] = useState(editing);   // once the user picks the sheet type by hand it is never changed automatically

  useEffect(() => { if (!editing) localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); }, [form, editing]);

  const handleChange = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleInput = (e) => handleChange(e.target.name, e.target.value);

  // Sheet type (official ONAV form): the common marks are kept, the ones above the new maximum are clamped
  const chooseVariant = (variant) => {
    setVariantTouched(true);
    setForm((prev) => (normalizeVariant(prev.variant) === variant ? prev : clampToVariant(prev, variant)));
  };

  // The sparkling form is SUGGESTED when the wine type is sparkling / semi-sparkling (never forced once the user chose)
  useEffect(() => {
    if (variantTouched || !form.wine_type) return;
    const suggested = suggestVariant(form.wine_type);
    setForm((prev) => (normalizeVariant(prev.variant) === suggested ? prev : clampToVariant(prev, suggested)));
  }, [form.wine_type, variantTouched]);

  const handleReset = () => {
    if (!window.confirm(t('onav.confirm_reset'))) return;
    setForm(INITIAL);
    setVariantTouched(false);
    setAttachmentItems([]);
    setLabelPhoto(null);
    localStorage.removeItem(DRAFT_KEY);
  };

  const variant = normalizeVariant(form.variant);
  const layout = layoutOf(variant);
  const totals = calcTotals(form);
  const slider = (row) => <MarkChips key={row.key} name={row.key} value={form[row.key]} max={row.max} variant={variant} onChange={handleChange} />;
  // Rows holding a value that is not printed on the form (sheets saved before the rule): the save is blocked until they are fixed
  const legacyRows = nonPrintedRows(form, variant);
  const sectionOf = (key) => ['visual', 'olfactory', 'gustatory', 'final'].find((sec) => layout[sec].some((r) => r.key === key));
  const legacyLabels = legacyRows.map((r) => `${t(`onav.sec.${sectionOf(r.key)}`)} – ${t(`onav.p.${r.key}`)} (${r.value})`).join('; ');
  const blockedByLegacy = () => {
    if (legacyRows.length === 0) return false;
    const first = document.getElementById(`onav-mark-${legacyRows[0].key}`);
    if (first) { first.scrollIntoView({ block: 'center' }); const chip = first.querySelector('[role=radio][tabindex="0"]'); if (chip) chip.focus(); }
    return true;
  };
  // numeric marks (a mark not selected yet counts as 0): totals, payload and chart
  const numericMarks = Object.fromEntries(ALL_MARKS.map((k) => [k, isBlankMark(form[k]) ? 0 : form[k]]));
  const row = (key) => layout.visual.concat(layout.olfactory, layout.gustatory, layout.final).find((r) => r.key === key);
  // Weight of a section on the overall quality of a wine (vademecum 'TOT' rows), for the variant of the sheet
  const weightLine = (section) => {
    const w = SECTION_WEIGHTS[variant];
    const text = section === 'gustatory'
      ? t('onav.help.weight_gustatory', { pct: w.gustatory, a: w.gustatoryParts.taste, b: w.gustatoryParts.retro })
      : t('onav.help.weight', { pct: w[section] });
    return <small className="onav-section-weight">{text}</small>;
  };

  // Sheet data in the API format (used both by the save and by the PDF export)
  const buildData = () => {
    if (!form.wine_name.trim()) { alert(t('onav.alert_wine_name')); return null; }
    if (blockedByLegacy()) return null;
    const data = {
      ...form,
      ...numericMarks,
      vintage: form.vintage ? parseInt(form.vintage, 10) : null,
      abv: form.abv ? parseFloat(form.abv) : null,
      serving_temperature: form.serving_temperature ? parseFloat(form.serving_temperature) : null,
      variant,
      perlage_size: variant === 'sparkling' ? numericMarks.perlage_size : null,                 // perlage exists only on the sparkling form
      perlage_persistence: variant === 'sparkling' ? numericMarks.perlage_persistence : null,
      gustatory_aftertaste: variant === 'sparkling' ? 0 : numericMarks.gustatory_aftertaste,    // the sparkling form has no aftertaste
      total_visual: totals.visual,
      total_olfactory: totals.olfactory,
      total_gustatory: totals.gustatory,
      total_final: totals.final,
      total: totals.total,
      verdict: totals.verdict,
    };
    Object.keys(data).forEach((k) => { if (typeof data[k] === 'string') data[k] = emptyToNull(data[k]); });
    return data;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = buildData();
    if (!data) return;
    const photo = editing ? null : labelPhoto;
    Promise.resolve(onSubmit(data, attachmentItems)).then(() => uploadWineLabel({ name: data.wine_name, vintage: data.vintage, producer: data.producer }, photo));
    if (editing) return;
    setForm(INITIAL);
    setVariantTouched(false);
    setAttachmentItems([]);
    setLabelPhoto(null);
    localStorage.removeItem(DRAFT_KEY);
  };

  const handleExportPdf = () => {
    const data = buildData();
    if (data) exportPdf({ path: 'onav', data, errorMessage: t('ui.export_error') });
  };

  const selectedAromas = parseAromaTypes(form.aroma_types);

  return (
    <form className="sheet-form" onSubmit={handleSubmit}>
      {/* ── General information ── */}
      <fieldset>
        <legend>{t('onav.sec.info')}</legend>
        <div className="form-field onav-variant">
          <label>{t('onav.variant.title')}</label>
          <div className="chip-group" role="radiogroup" aria-label={t('onav.variant.title')}>
            {['still', 'sparkling'].map((v) => (
              <button type="button" key={v} role="radio" aria-checked={variant === v}
                className={`chip ${variant === v ? 'selected' : ''}`} onClick={() => chooseVariant(v)}>
                {t(`onav.variant.${v}`)}
              </button>
            ))}
          </div>
          <small className="import-hint">{variant === 'sparkling' ? t('onav.variant.hint_sparkling') : t('onav.variant.hint')} {t('onav.variant.switch_note')}</small>
          <small className="import-hint onav-marks-hint">{variant === 'sparkling' ? t('onav.mark.hint_sparkling') : t('onav.mark.hint_still')}</small>
        </div>
        <LabelScanner kind="wine" onPhoto={setLabelPhoto}
          onApply={(values, scan, options) => setForm((prev) => applyScanToWineForm(prev, values, options))} />
        <div className="form-grid">
          <div className="form-field">
            <label>{t('onav.f.wine_name')}</label>
            <WineAutocomplete
              value={form.wine_name}
              onChange={(v) => handleChange('wine_name', v)}
              onSelect={(wine) => setForm((prev) => ({
                ...prev,
                wine_name: wine.name,
                vintage: wine.vintage || '',
                producer: wine.producer || '',
                classification: wine.classification || prev.classification,
                wine_type: wine.wine_type || prev.wine_type,
                abv: wine.abv != null ? wine.abv : prev.abv,
              }))}
              required
            />
          </div>
          <div className="form-field">
            <label>{t('onav.f.classification')}</label>
            <select name="classification" value={form.classification} onChange={handleInput}>
              <option value="">—</option>
              {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{tc('classification', c)}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>{t('onav.f.wine_type')}</label>
            <div className="chip-group">
              {WINE_TYPES.map((type) => (
                <button type="button" key={type}
                  className={`chip ${form.wine_type === type ? 'selected' : ''}`}
                  onClick={() => handleChange('wine_type', type)}>{tc('wine_type', type)}</button>
              ))}
            </div>
          </div>
          <div className="form-field">
            <label>{t('onav.f.vintage')}</label>
            <input name="vintage" type="number" min={1900} max={2030} value={form.vintage} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('onav.f.producer')}</label>
            <input name="producer" value={form.producer} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('onav.f.abv')}</label>
            <input name="abv" type="number" step={0.1} min={0} max={20} value={form.abv} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('onav.f.serving_temperature')}</label>
            <input name="serving_temperature" type="number" step={0.5} min={0} max={30} value={form.serving_temperature} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('onav.f.tasting_date')}</label>
            <input name="tasting_date" type="date" value={form.tasting_date} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('onav.f.venue')}</label>
            <input name="venue" value={form.venue} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('onav.f.taster')}</label>
            <input name="taster" value={form.taster} onChange={handleInput} />
          </div>
          {variant === 'sparkling' && <WineColorSelect value={form.wine_color} onChange={handleInput} />}
        </div>

        {/* Header fields of the official ONAV form (all optional) */}
        <details className="onav-competition">
          <summary>{t('onav.sec.competition')}</summary>
          <div className="form-grid">
            <div className="form-field">
              <label>{t('onav.f.event')}</label>
              <input name="event" value={form.event} onChange={handleInput} maxLength={200} />
            </div>
            <div className="form-field">
              <label>{t('onav.f.commission_number')}</label>
              <input name="commission_number" value={form.commission_number} onChange={handleInput} maxLength={50} />
            </div>
            <div className="form-field">
              <label>{t('onav.f.sample_number')}</label>
              <input name="sample_number" value={form.sample_number} onChange={handleInput} maxLength={50} />
            </div>
            <div className="form-field">
              <label>{t('onav.f.presentation_category')}</label>
              <input name="presentation_category" value={form.presentation_category} onChange={handleInput} maxLength={100} />
            </div>
            <div className="form-field">
              <label>{t('onav.f.tasting_time')}</label>
              <input name="tasting_time" type="time" value={form.tasting_time} onChange={handleInput} />
            </div>
            {variant !== 'sparkling' && <WineColorSelect value={form.wine_color} onChange={handleInput} />}
          </div>
        </details>
      </fieldset>

      {/* ── Visual evaluation ── */}
      <fieldset>
        <legend>{t('onav.sec.visual')} ({t('onav.max', { n: layout.totals.visual })})</legend>
        {weightLine('visual')}
        {variant === 'sparkling' ? (
          <>
            {slider(row('visual_clarity'))}
            {slider(row('perlage_size'))}
            {slider(row('perlage_persistence'))}
            {slider(row('visual_hue'))}
            <ColorPalette wineType={form.wine_type || 'sparkling'} />
            {slider(row('visual_intensity'))}
          </>
        ) : (
          <>
            {slider(row('visual_hue'))}
            <ColorPalette wineType={form.wine_type} />
            {slider(row('visual_intensity'))}
            {slider(row('visual_clarity'))}
          </>
        )}
        <div className="partial-total">{t('onav.partial_total')} <strong>{totals.visual}/{layout.totals.visual}</strong></div>
      </fieldset>

      {/* ── Olfactory evaluation ── */}
      <fieldset>
        <legend>{t('onav.sec.olfactory')} ({t('onav.max', { n: layout.totals.olfactory })})</legend>
        {weightLine('olfactory')}
        {layout.olfactory.map(slider)}
        <div className="partial-total">{t('onav.partial_total')} <strong>{totals.olfactory}/{layout.totals.olfactory}</strong></div>

        <div className="form-field" style={{ marginTop: 12 }}>
          <label>{t('onav.f.aroma_types')}</label>
          <div className="chip-group">
            {AROMA_TYPES.map((aroma) => {
              const isSelected = selectedAromas.includes(aroma);
              return (
                <button type="button" key={aroma}
                  className={`chip ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    const next = isSelected ? selectedAromas.filter((x) => x !== aroma) : [...selectedAromas, aroma];
                    handleChange('aroma_types', joinAromaTypes(next));
                  }}>{tc('aroma', aroma)}</button>
              );
            })}
          </div>
        </div>
      </fieldset>

      {/* ── Gustatory evaluation ── */}
      <fieldset>
        <legend>{t('onav.sec.gustatory')} ({t('onav.max', { n: layout.totals.gustatory })})</legend>
        {weightLine('gustatory')}
        {slider(row('gustatory_cleanness'))}
        {slider(row('gustatory_intensity'))}
        {slider(row('gustatory_body'))}

        <h4 className="section-subtitle">{t('onav.sec.balance')}</h4>
        <div className="form-grid">
          <div className="form-field"><label>{t('onav.p.balance_acidity')}</label>
            <input name="balance_acidity" value={form.balance_acidity} onChange={handleInput} placeholder={t('onav.ph.balance_acidity')} /></div>
          <div className="form-field"><label>{t('onav.p.balance_tannicity')}</label>
            <input name="balance_tannicity" value={form.balance_tannicity} onChange={handleInput} placeholder={t('onav.ph.balance_tannicity')} /></div>
          <div className="form-field"><label>{t('onav.p.balance_alcohol')}</label>
            <input name="balance_alcohol" value={form.balance_alcohol} onChange={handleInput} placeholder={t('onav.ph.balance_alcohol')} /></div>
          <div className="form-field"><label>{t('onav.p.balance_softness')}</label>
            <input name="balance_softness" value={form.balance_softness} onChange={handleInput} placeholder={t('onav.ph.balance_softness')} /></div>
        </div>

        {slider(row('gustatory_harmony'))}
        {slider(row('gustatory_persistence'))}
        {variant === 'still' && slider(row('gustatory_aftertaste'))}
        <div className="partial-total">{t('onav.partial_total')} <strong>{totals.gustatory}/{layout.totals.gustatory}</strong></div>
      </fieldset>

      {/* ── Final judgement ── */}
      <fieldset>
        <legend>{t('onav.sec.final')} ({t('onav.max', { n: layout.totals.final })})</legend>
        {weightLine('final')}
        {slider(row('overall_impression'))}
        <div className="partial-total">{t('onav.partial_total')} <strong>{totals.final}/{layout.totals.final}</strong></div>
      </fieldset>

      {/* ── Result ── */}
      <fieldset>
        <legend>{t('onav.sec.result')}</legend>
        <div className="onav-total">
          <div className="onav-total-grid">
            <span>{t('onav.res.visual')}</span><strong>{totals.visual}/{layout.totals.visual}</strong>
            <span>{t('onav.res.olfactory')}</span><strong>{totals.olfactory}/{layout.totals.olfactory}</strong>
            <span>{t('onav.res.gustatory')}</span><strong>{totals.gustatory}/{layout.totals.gustatory}</strong>
            <span>{t('onav.res.verdict')}</span><strong>{totals.final}/{layout.totals.final}</strong>
          </div>
          <div className="onav-final-total">
            <span className="onav-score">{totals.total}/100</span>
            {totals.verdict && (
              <span className={`pairing-badge ${totals.verdict}`}>{tc('verdict', totals.verdict)}</span>
            )}
          </div>
        </div>
        <div className="form-field">
          <label>{t('onav.f.notes')}</label>
          <textarea name="notes" value={form.notes} onChange={handleInput} rows={3} placeholder={t('onav.ph.notes')} />
        </div>
      </fieldset>

      {/* ── Graphic sheet ── */}
      <fieldset>
        <legend>{t('onav.sec.chart')}</legend>
        <OnavChart data={{
          ...form,
          ...numericMarks,
          variant,
          total_visual: totals.visual,
          total_olfactory: totals.olfactory,
          total_gustatory: totals.gustatory,
          total_final: totals.final,
          verdict: totals.verdict,
        }} />
      </fieldset>

      {/* ── Attachments (only when creating: an existing sheet manages them in its detail view) ── */}
      {!editing && (
        <fieldset>
          <legend>{t('attachments.title')}</legend>
          <AttachmentPicker items={attachmentItems} onChange={setAttachmentItems} />
        </fieldset>
      )}

      <div className="form-field publish-opt">
        <label className="publish-label">
          <input type="checkbox" checked={form.visibility === 'public'}
            onChange={(e) => handleChange('visibility', e.target.checked ? 'public' : 'private')} />
          {' '}{t('ui.publish_label')}
        </label>
        <small className="import-hint">{t('ui.publish_hint')}</small>
      </div>

      {legacyRows.length > 0 && <div className="message error" role="alert" id="onav-legacy-summary">{t('onav.mark.blocked', { rows: legacyLabels })}</div>}

      <div className="form-buttons">
        <button type="submit" className="submit-btn">{editing ? t('onav.save_edit') : t('onav.save_new')}</button>
        <button type="button" className="reset-btn" onClick={handleExportPdf}>{t('ui.export_pdf')}</button>
        {editing
          ? <button type="button" className="reset-btn" onClick={onCancel}>{t('ui.cancel')}</button>
          : <button type="button" className="reset-btn" onClick={handleReset}>{t('ui.reset')}</button>}
      </div>
    </form>
  );
}

export default OnavForm;
